import { renderTopbar } from '../components/topbar.js';
import { watchCourses, createCourse, updateCourse, trashCourse, restoreCourse } from '../lib/courses.js';
import { currentUser, isSchoolOwnerCached } from '../lib/auth.js';
import { navigate } from '../router.js';
import { formatDaysRemaining } from '../lib/trash.js';
import { deleteCourseForever, watchMyPurchases, isExpired } from '../lib/marketplace.js';
import { openPurchaseViewer } from './marketplace.js';
import { confirmDialog, alertDialog } from '../lib/dialog.js';
import { createIdempotencyKey } from '../lib/idempotency.js';
import { COURSE_EMOJIS } from '../lib/courseEmojis.js';
import { friendlyError } from '../lib/friendlyError.js';
import { attachSchoolSubscriptionQuizScoreBridge } from '../lib/quizScore.js';
import { attachAskAIBridge } from '../lib/askAI.js';
import { loadReadOnlyFrame } from '../lib/readOnlyFrame.js';
import { pinKey, setPinned, watchPinnedItems } from '../lib/pins.js';
import { searchAllMyTopics } from '../lib/topics.js';
import { firstContentItem, hasTreeStructure, renderContentTree, wireContentTree } from '../lib/contentTree.js';

const TOPIC_ICON = '🧩'; // matches views/course.js's own topic-row icon

// Stable sort (Array.prototype.sort preserves relative order of equal
// elements in every modern engine) — pinned items float to the top of
// whichever section they're already in, without otherwise reshuffling it.
function sortPinnedFirst(items, kind, idField, pinnedKeys) {
  return [...items].sort((a, b) => {
    const pa = pinnedKeys.has(pinKey(kind, a[idField])) ? 0 : 1;
    const pb = pinnedKeys.has(pinKey(kind, b[idField])) ? 0 : 1;
    return pa - pb;
  });
}

function pinButtonHtml(kind, id, pinnedKeys) {
  const isPinned = pinnedKeys.has(pinKey(kind, id));
  return `<button class="icon-btn pin-toggle-btn" data-pin-kind="${kind}" data-pin-id="${id}" data-pinned="${isPinned}" title="${isPinned ? 'Unpin' : 'Pin to top'}">${isPinned ? '📌' : '📍'}</button>`;
}

function wirePinButtons(container, uid) {
  container.querySelectorAll('.pin-toggle-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isPinned = btn.dataset.pinned === 'true';
      setPinned(uid, pinKey(btn.dataset.pinKind, btn.dataset.pinId), !isPinned).catch(() => {});
    });
  });
}

export function renderHome(container) {
  container.innerHTML = '';
  container.appendChild(renderTopbar('/'));

  const main = document.createElement('div');
  main.className = 'main-full';
  // School owners get a shortcut card to their dashboard — their home is
  // otherwise identical to any learner's, and the dashboard is where seats,
  // wallet, and teachers actually live.
  const schoolBanner = isSchoolOwnerCached() ? `
    <div class="settings-panel" id="school-owner-banner" style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;margin-bottom:16px;cursor:pointer;">
      <div style="display:flex;align-items:center;gap:12px;">
        <div style="font-size:1.8rem;">🏫</div>
        <div>
          <strong>You manage a school on Simustage</strong>
          <p style="color:var(--text-dim);margin:2px 0 0;font-size:0.9rem;">Seats, term, wallet balance, and teachers — all in your school dashboard.</p>
        </div>
      </div>
      <button class="btn btn-primary" id="school-owner-open">Open school dashboard</button>
    </div>` : '';
  main.innerHTML = `
    ${schoolBanner}
    <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;">
      <div>
        <h1 style="margin-bottom:4px;">Your courses</h1>
        <p style="color:var(--text-dim);margin-top:0;">Create a course, then build topics inside it by chatting with the AI.</p>
      </div>
      <div style="display:flex;gap:8px;">
        <button class="btn" id="trash-btn">🗑️ Recycle Bin</button>
        <button class="btn btn-primary" id="new-course-btn">+ New course</button>
      </div>
    </div>
    <div class="field" style="max-width:420px;margin:14px 0 0;">
      <input type="text" id="topic-name-search-input" placeholder="🔎 Search all your topics by name…" autocomplete="off" />
    </div>
    <div id="topic-name-search-results" style="display:none;max-width:420px;margin-bottom:8px;"></div>
    <div id="school-subscribed-section" style="display:none;margin:18px 0;">
      <h3 style="margin-bottom:8px;">🏫 Subscribed by School</h3>
      <div class="grid-cards" id="school-subscribed-grid"></div>
    </div>
    <div id="purchased-section" style="display:none;margin:18px 0;">
      <h3 style="margin-bottom:8px;">🧾 My Library</h3>
      <div class="grid-cards" id="purchased-grid"></div>
    </div>
    <div class="grid-cards" id="course-grid"></div>
  `;
  container.appendChild(main);

  const uid = currentUser().uid;

  // Pinning ("appear on top always" — a plain per-student preference, see
  // lib/pins.js) spans all three sections below, each independently data-
  // sourced, so each keeps its own latest-data cache and re-renders off
  // BOTH its own data changing AND pinnedKeys changing (watched once, at
  // the bottom of this function, after every render function below exists).
  let pinnedKeys = new Set();
  let latestSubs = [];
  let latestPurchases = [];

  // "Subscribed by School" — free, read-only (for everyone but the school
  // owner, who instead sees their own amendable copy as a normal course
  // above, tagged via the course-level badge in views/course.js) courses
  // the caller's school has subscribed to. Silently does nothing for an
  // account with no school link at all (listMySchoolSubscriptions throws
  // failed-precondition in that case) — this section just never appears.
  let getSchoolSubscriptionContentFn = null;
  function renderSubsSection() {
    if (latestSubs.length === 0) return;
    const section = main.querySelector('#school-subscribed-section');
    const grid = main.querySelector('#school-subscribed-grid');
    section.style.display = 'block';
    const subs = sortPinnedFirst(latestSubs, 'schoolSub', 'listingId', pinnedKeys);
    grid.innerHTML = subs.map((s) => `
      <div class="card" data-listing="${escapeHtml(s.listingId)}">
        <div style="display:flex;justify-content:flex-end;">${pinButtonHtml('schoolSub', s.listingId, pinnedKeys)}</div>
        <div class="card-emoji">${s.emoji || '📘'}</div>
        <h3>${escapeHtml(s.title)}</h3>
        <p>${escapeHtml(s.description || 'No description')}</p>
        <p style="color:var(--text-dim);font-size:0.85rem;">${s.viewOnly ? 'View-only' : 'Amendable by your school'} · free via your school</p>
      </div>
    `).join('');
    wirePinButtons(grid, uid);
    grid.querySelectorAll('.card').forEach((cardEl) => {
      cardEl.addEventListener('click', async (e) => {
        if (e.target.closest('.pin-toggle-btn')) return;
        if (cardEl.dataset.loading === 'true') return; // ignore a second click while already opening
        // Same real network fetch (getSchoolSubscriptionContent reads the
        // whole course's content) as the topic-search "Subscribed by
        // school" branch above — show the same waiting state here too,
        // instead of leaving the click looking like a no-op.
        cardEl.dataset.loading = 'true';
        cardEl.style.opacity = '0.6';
        cardEl.style.cursor = 'wait';
        try {
          const content = await getSchoolSubscriptionContentFn(cardEl.dataset.listing);
          openSchoolSubscriptionViewer(content, cardEl.dataset.listing);
        } catch (err) {
          // Best-effort — a stale/removed subscription just fails silently
          // to open rather than breaking the whole home page.
        } finally {
          cardEl.dataset.loading = 'false';
          cardEl.style.opacity = '';
          cardEl.style.cursor = '';
        }
      });
    });
  }
  import('../lib/schoolMarketplace.js').then(({ listMySchoolSubscriptions, getSchoolSubscriptionContent }) => {
    getSchoolSubscriptionContentFn = getSchoolSubscriptionContent;
    listMySchoolSubscriptions().then((data) => {
      latestSubs = data.subscriptions || [];
      renderSubsSection();
    }).catch(() => {});
  });

  // "Purchased" — view-only purchases, nothing ever written to this buyer's
  // own courses collection. The remixCourseId filter below is legacy-only
  // now (that "extend it further" mechanism was removed — see
  // functions/marketplace.js listForSaleImpl): a purchase from before its
  // removal that DID get a copy still shows up as a real, editable course in
  // the "Your courses" grid below instead, so it stays filtered out here to
  // avoid a confusing duplicate. Opens the same read-only viewer as Library →
  // My purchases (openPurchaseViewer, src/views/marketplace.js) rather than
  // duplicating that modal here.
  const purchasedSection = main.querySelector('#purchased-section');
  const purchasedGrid = main.querySelector('#purchased-grid');
  function renderPurchasedSection() {
    if (latestPurchases.length === 0) {
      purchasedSection.style.display = 'none';
      return;
    }
    purchasedSection.style.display = 'block';
    const viewOnly = sortPinnedFirst(latestPurchases, 'purchase', 'id', pinnedKeys);
    purchasedGrid.innerHTML = viewOnly.map((p) => {
      const expired = isExpired(p);
      return `
      <div class="card${expired ? ' is-disabled' : ''}" data-purchase="${p.id}" data-listing="${p.listingId}" style="${expired ? 'opacity:0.55;cursor:default;' : ''}">
        <div style="display:flex;justify-content:flex-end;">${pinButtonHtml('purchase', p.id, pinnedKeys)}</div>
        <div class="card-emoji">${p.listingEmoji || '📘'}</div>
        <h3>${escapeHtml(p.listingTitle || 'Untitled')}</h3>
        <span class="pill" style="display:inline-block;margin:4px 0;" title="From the Library — view-only, not one of your own courses">Library item</span>
        <p>${escapeHtml(p.listingDescription || 'No description')}</p>
        <p style="color:var(--text-dim);font-size:0.85rem;">${expired ? 'Access expired' : 'View-only · not editable'}</p>
      </div>
    `;
    }).join('');
    wirePinButtons(purchasedGrid, uid);
    purchasedGrid.querySelectorAll('.card').forEach((cardEl) => {
      cardEl.addEventListener('click', (e) => {
        if (e.target.closest('.pin-toggle-btn')) return;
        const purchase = viewOnly.find((p) => p.id === cardEl.dataset.purchase);
        if (!purchase || isExpired(purchase)) return;
        openPurchaseViewer(uid, cardEl.dataset.purchase, cardEl.dataset.listing);
      });
    });
  }
  const unsubPurchases = watchMyPurchases(uid, (purchases) => {
    latestPurchases = purchases.filter((p) => !p.remixCourseId);
    renderPurchasedSection();
  });

  const grid = main.querySelector('#course-grid');
  const trashBtn = main.querySelector('#trash-btn');
  const ownerBanner = main.querySelector('#school-owner-banner');
  if (ownerBanner) ownerBanner.addEventListener('click', () => navigate('/school'));
  let latestCourses = [];
  const trashListeners = new Set();

  function renderGrid(active) {
    const sorted = sortPinnedFirst(active, 'course', 'id', pinnedKeys);
    grid.innerHTML = sorted.map((c) => `
      <div class="card" data-id="${c.id}">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;">
          <div class="card-emoji">${c.emoji || '📘'}</div>
          <div style="display:flex;gap:4px;">
            ${pinButtonHtml('course', c.id, pinnedKeys)}
            <button class="icon-btn edit-course" data-id="${c.id}" title="Edit course">✏️</button>
            <button class="icon-btn delete-course" data-id="${c.id}" title="Delete course">✕</button>
          </div>
        </div>
        <h3>${escapeHtml(c.title)}</h3>
        ${c.sourceListingId ? `<span class="pill" style="display:inline-block;margin-bottom:4px;" title="${c.schoolCode ? 'Your school subscribed to this course from the Library' : 'Your own editable copy from the Library'}">${c.schoolCode ? '🏫 Subscribed' : '🛒 Library copy'}</span>` : ''}
        <p>${escapeHtml(c.description || 'No description')}</p>
      </div>
    `).join('');

    wirePinButtons(grid, uid);
    grid.querySelectorAll('.card').forEach((cardEl) => {
      cardEl.addEventListener('click', (e) => {
        if (e.target.classList.contains('delete-course') || e.target.closest('.pin-toggle-btn')) return;
        navigate(`/course/${cardEl.dataset.id}`);
      });
    });
    grid.querySelectorAll('.delete-course').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const course = active.find((c) => c.id === btn.dataset.id);
        const ok = await confirmDialog(
          `Move "${course?.title || 'this course'}" to the Recycle Bin? It will be permanently deleted in 7 days unless you restore it.`,
          { title: 'Move to Recycle Bin?' }
        );
        if (ok) {
          await trashCourse(uid, btn.dataset.id);
        }
      });
    });
    grid.querySelectorAll('.edit-course').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const course = active.find((c) => c.id === btn.dataset.id);
        if (course) openEditCourseModal(uid, course);
      });
    });
  }

  const unsub = watchCourses(uid, (courses) => {
    latestCourses = courses;
    trashListeners.forEach((fn) => fn(courses));
    const active = courses.filter((c) => !c.deletedAt);
    const trashedCount = courses.length - active.length;
    trashBtn.textContent = trashedCount > 0 ? `🗑️ Recycle Bin (${trashedCount})` : '🗑️ Recycle Bin';

    if (active.length === 0) {
      grid.innerHTML = `<div class="empty-state" style="grid-column:1/-1;">
        <div style="font-size:2.5rem;">📚</div>
        <h3>No courses yet</h3>
        <p>Create your first course to get started.</p>
      </div>`;
      return;
    }

    renderGrid(active);
  });

  main.querySelector('#new-course-btn').addEventListener('click', () => openCreateCourseModal(uid));
  trashBtn.addEventListener('click', () => openTrashModal(uid, latestCourses, trashListeners));

  // Cross-course topic-name search (see functions/search.js
  // searchAllMyTopics) — same debounced search-as-you-type shape as
  // views/course.js's own per-course topic search, but scoped to every
  // course this student owns PLUS every course their school subscribes to.
  // A result carries `listingId` only when it's school-subscribed content
  // (owned by a different account — the school owner or the original
  // seller); that's the signal this click handler uses to open the
  // read-only school viewer instead of navigating to /course/.../topic/...,
  // which only ever works for a course this account itself owns.
  // courseTitle/emoji for OWN results come from latestCourses (already
  // loaded above); school results already carry their own courseTitle/emoji
  // from searchMySchoolTopicsByTitle (functions/schoolMarketplace.js).
  const topicSearchInput = main.querySelector('#topic-name-search-input');
  const topicSearchResultsEl = main.querySelector('#topic-name-search-results');
  let topicSearchDebounce = null;
  let topicSearchToken = 0;
  let topicSearchResultsCache = [];
  topicSearchInput.addEventListener('input', () => {
    clearTimeout(topicSearchDebounce);
    const q = topicSearchInput.value.trim();
    if (q.length < 3) { topicSearchResultsEl.style.display = 'none'; topicSearchResultsEl.innerHTML = ''; return; }
    topicSearchDebounce = setTimeout(async () => {
      const token = ++topicSearchToken;
      topicSearchResultsEl.style.display = 'block';
      topicSearchResultsEl.innerHTML = `<div style="padding:6px 4px;color:var(--text-dim);font-size:0.8rem;">Searching…</div>`;
      try {
        const results = await searchAllMyTopics(q);
        if (token !== topicSearchToken) return;
        topicSearchResultsCache = results;
        topicSearchResultsEl.innerHTML = results.length
          ? results.map((r, i) => {
            let courseLabel;
            if (r.listingId) {
              courseLabel = `🏫 ${escapeHtml(r.courseTitle || 'Subscribed by school')}`;
            } else {
              const course = latestCourses.find((c) => c.id === r.courseId);
              courseLabel = course ? `${course.emoji || '📘'} ${escapeHtml(course.title)}` : 'Unknown course';
            }
            return `<div class="tree-item search-result-item" data-idx="${i}">
              ${TOPIC_ICON} ${escapeHtml(r.title)} <span style="color:var(--text-dim);font-size:0.8rem;margin-left:4px;">— ${courseLabel}</span>
            </div>`;
          }).join('')
          : `<div style="padding:6px 4px;color:var(--text-dim);font-size:0.8rem;">No matches.</div>`;
        topicSearchResultsEl.querySelectorAll('.search-result-item').forEach((el) => {
          el.addEventListener('click', async () => {
            if (el.dataset.loading === 'true') return; // ignore a second click while already opening
            const r = topicSearchResultsCache[Number(el.dataset.idx)];
            if (!r) return;
            // Only the school-subscribed branch has a real network fetch
            // before anything opens (getSchoolSubscriptionContent reads the
            // whole course's content) — own-topic navigation is an instant
            // client-side route change, but showing the same "opening…"
            // state either way is simpler than branching the UI too.
            const originalHtml = el.innerHTML;
            el.dataset.loading = 'true';
            el.style.opacity = '0.6';
            el.innerHTML = `${TOPIC_ICON} Opening…`;
            try {
              if (r.listingId) {
                if (!getSchoolSubscriptionContentFn) return; // still loading — rare, just no-op rather than crash
                const content = await getSchoolSubscriptionContentFn(r.listingId);
                openSchoolSubscriptionViewer(content, r.listingId, r.id);
              } else {
                navigate(`/course/${r.courseId}/topic/${r.id}`);
              }
            } catch (err) { /* best-effort, same posture as the "Subscribed by School" cards above */
            } finally {
              el.dataset.loading = 'false';
              el.style.opacity = '';
              el.innerHTML = originalHtml;
            }
          });
        });
      } catch (err) {
        if (token !== topicSearchToken) return;
        topicSearchResultsEl.innerHTML = `<div style="padding:6px 4px;color:var(--text-dim);font-size:0.8rem;">${escapeHtml(friendlyError(err, 'Search failed.'))}</div>`;
      }
    }, 400);
  });

  // Single live watcher for all three sections above — each just re-renders
  // off its own already-cached latest data (see each section's own comment).
  const unsubPins = watchPinnedItems(uid, (keys) => {
    pinnedKeys = keys;
    renderSubsSection();
    renderPurchasedSection();
    const active = latestCourses.filter((c) => !c.deletedAt);
    if (active.length > 0) renderGrid(active);
  });

  return () => { unsub(); unsubPurchases(); unsubPins(); };
}

function openTrashModal(uid, initialCourses, trashListeners) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <h2>🗑️ Recycle Bin</h2>
      <p style="color:var(--text-dim);margin-top:-8px;">Deleted courses stay here for 7 days before being permanently removed.</p>
      <div id="trash-list" style="display:flex;flex-direction:column;gap:8px;max-height:50vh;overflow:auto;"></div>
      <div class="modal-actions">
        <button class="btn" id="close-btn">Close</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  function close() {
    trashListeners.delete(onCoursesChanged);
    overlay.remove();
  }
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('#close-btn').addEventListener('click', close);

  let currentCourses = initialCourses;
  function onCoursesChanged(courses) {
    currentCourses = courses;
    render();
  }
  trashListeners.add(onCoursesChanged);

  function render() {
    const trashed = currentCourses.filter((c) => c.deletedAt);
    const list = overlay.querySelector('#trash-list');
    if (trashed.length === 0) {
      list.innerHTML = `<p style="color:var(--text-dim);">Recycle Bin is empty.</p>`;
      return;
    }
    list.innerHTML = trashed.map((c) => `
      <div class="card" style="display:flex;justify-content:space-between;align-items:center;gap:10px;padding:10px 14px;" data-id="${c.id}">
        <div>
          <strong>${c.emoji || '📘'} ${escapeHtml(c.title)}</strong>
          <div style="color:var(--text-dim);font-size:0.82rem;">Permanently deleted in ${formatDaysRemaining(c.deletedAt)}</div>
        </div>
        <div style="display:flex;gap:6px;">
          <button class="btn btn-sm restore-btn" data-id="${c.id}">Restore</button>
          <button class="btn btn-sm btn-danger purge-btn" data-id="${c.id}">Delete forever</button>
        </div>
      </div>
    `).join('');

    list.querySelectorAll('.restore-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        await restoreCourse(uid, btn.dataset.id);
      });
    });
    list.querySelectorAll('.purge-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const course = trashed.find((c) => c.id === btn.dataset.id);
        const ok = await confirmDialog(
          `Permanently delete "${course?.title || 'this course'}"? This cannot be undone.`,
          { title: 'Delete forever?', okText: 'Delete forever', danger: true }
        );
        if (!ok) return;
        btn.disabled = true;
        try {
          // Guarded server-side: refuses to delete while any buyer still
          // has active access to purchased content from this course (see
          // functions/contentLifecycle.js).
          await deleteCourseForever(btn.dataset.id);
        } catch (e) {
          await alertDialog(friendlyError(e, 'Could not delete this course.'), { title: "Can't delete" });
          btn.disabled = false;
        }
      });
    });
  }

  render();
}

function openCreateCourseModal(uid) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <h2>New course</h2>
      <div class="field"><label>Title</label><input type="text" id="c-title" placeholder="Geometry Lab" /></div>
      <div class="field"><label>Description (optional)</label><input type="text" id="c-desc" placeholder="Interactive geometry lessons" /></div>
      <div class="field">
        <label>Icon</label>
        <div class="emoji-picker">
          ${COURSE_EMOJIS.map((e, i) => `<button class="btn btn-sm emoji-choice" data-emoji="${e}" style="${i === 0 ? 'border-color:var(--accent)' : ''}">${e}</button>`).join('')}
        </div>
      </div>
      <div class="modal-actions">
        <button class="btn" id="cancel-btn">Cancel</button>
        <button class="btn btn-primary" id="create-btn">Create</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  let chosenEmoji = COURSE_EMOJIS[0];
  let createKey = createIdempotencyKey('createCourse');

  overlay.querySelectorAll('.emoji-choice').forEach((btn) => {
    btn.addEventListener('click', () => {
      chosenEmoji = btn.dataset.emoji;
      overlay.querySelectorAll('.emoji-choice').forEach((b) => b.style.borderColor = 'var(--border)');
      btn.style.borderColor = 'var(--accent)';
    });
  });

  overlay.querySelector('#cancel-btn').addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  overlay.querySelector('#create-btn').addEventListener('click', async () => {
    const createBtn = overlay.querySelector('#create-btn');
    const title = overlay.querySelector('#c-title').value.trim();
    if (!title) return;
    createBtn.disabled = true;
    const description = overlay.querySelector('#c-desc').value.trim();
    try {
      const id = await createCourse(uid, { title, description, emoji: chosenEmoji, idempotencyKey: createKey });
      overlay.remove();
      navigate(`/course/${id}`);
    } catch (err) {
      createKey = createIdempotencyKey('createCourse');
      createBtn.disabled = false;
      alertDialog(friendlyError(err, 'Could not create course.'));
    }
  });
}

// Shared "Edit course" modal — lets the owner rename the course and/or
// change its icon. Used both from the course grid (edit-course button) and
// from inside a course page (views/course.js). `onSaved(patch)` fires after
// a successful write so the caller can update its own in-memory copy/DOM
// without needing a live subscription just for this.
export function openEditCourseModal(uid, course, onSaved) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <h2>Edit course</h2>
      <div class="field"><label>Title</label><input type="text" id="c-title" value="${escapeHtml(course.title || '')}" /></div>
      <div class="field"><label>Description (optional)</label><input type="text" id="c-desc" value="${escapeHtml(course.description || '')}" /></div>
      <div class="field">
        <label>Icon</label>
        <div class="emoji-picker">
          ${COURSE_EMOJIS.map((e) => `<button class="btn btn-sm emoji-choice" data-emoji="${e}" style="${e === (course.emoji || COURSE_EMOJIS[0]) ? 'border-color:var(--accent)' : ''}">${e}</button>`).join('')}
        </div>
      </div>
      <div id="e-error" style="color:var(--danger);font-size:0.85rem;min-height:1.2em;"></div>
      <div class="modal-actions">
        <button class="btn" id="cancel-btn">Cancel</button>
        <button class="btn btn-primary" id="save-btn">Save changes</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  let chosenEmoji = course.emoji || COURSE_EMOJIS[0];

  overlay.querySelectorAll('.emoji-choice').forEach((btn) => {
    btn.addEventListener('click', () => {
      chosenEmoji = btn.dataset.emoji;
      overlay.querySelectorAll('.emoji-choice').forEach((b) => b.style.borderColor = 'var(--border)');
      btn.style.borderColor = 'var(--accent)';
    });
  });

  overlay.querySelector('#cancel-btn').addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  overlay.querySelector('#save-btn').addEventListener('click', async () => {
    const errEl = overlay.querySelector('#e-error');
    errEl.textContent = '';
    const saveBtn = overlay.querySelector('#save-btn');
    const title = overlay.querySelector('#c-title').value.trim();
    if (!title) { errEl.textContent = 'Title is required.'; return; }
    const description = overlay.querySelector('#c-desc').value.trim();
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving…';
    try {
      const patch = { title, description, emoji: chosenEmoji };
      await updateCourse(uid, course.id, patch);
      overlay.remove();
      onSaved?.(patch);
    } catch (err) {
      errEl.textContent = friendlyError(err, 'Could not save changes.');
      saveBtn.disabled = false;
      saveBtn.textContent = 'Save changes';
    }
  });
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// Read-only viewer for a "Subscribed by School" course — same sandboxed-
// iframe-per-topic idea as the marketplace purchase viewer (openPurchaseViewer,
// views/marketplace.js), just backed by getSchoolSubscriptionContent instead
// of getPurchaseContent, and same modal-fullscreen treatment (a 640px
// modal-wide box left this feeling cramped next to every other full-page
// content view). No chat/generation controls here at all — amending happens
// by the school owner opening their own copy in the normal Studio (see
// views/course.js), not from this view. window.fetchContentBank IS wired
// (bankOnly attachAskAIBridge, see lib/askAI.js) since a bank-backed practice
// topic (e.g. any of the "___ Types" grammar drills) is otherwise unusable
// here — it's a free Firestore read, not generation, so it doesn't conflict
// with the "no chat/generation" rule; window.askAI/askAIContent stay blocked.
function openSchoolSubscriptionViewer({ title, courseId, creatorUid, items }, listingId, initialTopicId = null) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal modal-fullscreen" style="display:flex;flex-direction:column;">
      <div class="modal-fullscreen-header" style="display:flex;justify-content:space-between;align-items:center;">
        <h2 style="margin:0;">${escapeHtml(title || 'Subscribed course')}</h2>
        <button class="btn btn-sm" id="close-btn">✕</button>
      </div>
      <div class="modal-fullscreen-body">
        ${hasTreeStructure(items) ? `
          <div class="readonly-content-viewer">
            ${renderContentTree(items, initialTopicId || firstContentItem(items)?.topicId)}
            <div style="flex:1;min-height:0;position:relative;background:#fff;">
              <iframe id="school-sub-frame" sandbox="allow-scripts" allowfullscreen allow="fullscreen" style="position:absolute;inset:0;width:100%;height:100%;border:none;"></iframe>
            </div>
          </div>
        ` : `
          <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:10px;flex-shrink:0;">
            ${(items || []).filter((it) => it.kind !== 'index' && it.html).map((it, i) => `<button class="btn btn-sm" data-topic-idx="${i}">${escapeHtml(it.title)}</button>`).join('')}
          </div>
          <div style="flex:1;min-height:0;position:relative;">
            <iframe id="school-sub-frame" sandbox="allow-scripts" allowfullscreen allow="fullscreen" style="position:absolute;inset:0;width:100%;height:100%;border:1px solid var(--border);border-radius:8px;"></iframe>
          </div>
        `}      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  let detachQuizScore = null;
  let detachAskAI = null;
  const closeOverlay = () => { detachQuizScore?.(); detachAskAI?.(); overlay.remove(); };
  overlay.querySelector('#close-btn').addEventListener('click', closeOverlay);

  const frame = overlay.querySelector('#school-sub-frame');
  const contentItems = (items || []).filter((it) => it.kind !== 'index' && it.html);
  function showItem(item) {
    if (!item || !frame) return;
    detachQuizScore?.();
    detachAskAI?.();
    loadReadOnlyFrame(frame, item.html, { courseId, topicId: item.topicId });
    detachQuizScore = attachSchoolSubscriptionQuizScoreBridge(frame, { listingId, topicId: item.topicId });
    // bankOnly: this view deliberately has no chat/generation controls (see
    // header comment) - window.fetchContentBank still works (free, no LLM
    // call), but window.askAI/askAIContent are rejected outright.
    detachAskAI = attachAskAIBridge(frame, {
      ownerUid: creatorUid, courseId, topicId: item.topicId, bankOnly: true,
    });
  }
  if (hasTreeStructure(items)) {
    wireContentTree(overlay.querySelector('.readonly-content-tree'), items, {
      initialTopicId: initialTopicId || firstContentItem(items)?.topicId,
      onSelect: showItem,
    });
  } else {
    overlay.querySelectorAll('[data-topic-idx]').forEach((btn) => {
      btn.addEventListener('click', () => showItem(contentItems[Number(btn.dataset.topicIdx)]));
    });
    const initialIdx = initialTopicId ? contentItems.findIndex((it) => it.topicId === initialTopicId) : -1;
    showItem(contentItems[initialIdx >= 0 ? initialIdx : 0]);
  }
}
